import Button from '@mui/material/Button';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ClaimsApi from '../../api/ClaimsApi';
import { handleUnauthorizedResponse } from '../../api/http';
import { AuthData } from '../../context/auth';
import { PolicyData, withPolicyContext } from '../../context/policy';
import { ClaimCreateRequest } from '../../types/claim';
import ClaimsTable from '../ClaimsTable';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../common';
import { errorToast, successToast } from '../Toast';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
}

const ClaimsView = (props: Props) => {
	const navigate = useNavigate();
	const { authData, policyData } = props;

	const { policy, setLoading } = policyData;

	const [claims, setClaims] = useState([]);

	useEffect(() => {
		const source = axios.CancelToken.source();

		async function getClaims() {
			try {
				setLoading(true);
				const claimsList = await ClaimsApi.getClaimsForPolicy(
					authData.auth.authorizationToken,
					policy.locator,
					{ cancelToken: source.token },
				);
				setClaims(claimsList.data);
				setLoading(false);
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				} else {
					await handleUnauthorizedResponse(error, authData);
					errorToast('Failed to fetch claims');
				}
			}
		}
		getClaims();

		return () => {
			source.cancel();
		};
	}, []);

	const fileClaim = async () => {
		try {
			setLoading(true);
			const claimCreateReq: ClaimCreateRequest = {
				policyLocator: policy.locator,
				fieldGroups: null,
			};
			const claim = await ClaimsApi.createClaim(
				authData.auth.authorizationToken,
				claimCreateReq,
			);
			successToast('Successfully created claim');
			setLoading(false);
			navigate(claim.data.locator);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to create claim');
		}
	};

	const FileClaimButton = () => {
		return (
			<Button
				style={{
					border: '1px solid #02457A',
					backgroundColor: '#FFFFF',
					color: '#02457A',
					margin: '5px',
				}}
				onClick={fileClaim}
			>
				File Claim
			</Button>
		);
	};

	const hasClaimPermissions = authData.hasPermission('claim.create');

	return (
		<>
			<StyledTitleContainer>
				<StyledTitle>Claims</StyledTitle>
				{hasClaimPermissions && FileClaimButton()}
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<ClaimsTable claims={claims} />
			</StyledSectionContainer>
		</>
	);
};

export default withPolicyContext<Props>(ClaimsView);
